import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { statsAPI, usersAPI } from '../../api/api';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './Dashboard.css';
import DateRangePicker from '../../components/DateRangePicker'; // ← Только этот импорт
import CustomDropdown from '../../components/CustomDropdown';

const Dashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(() => {
  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  return {
    startDate: new Date(year, month, 1), // 1 число текущего месяца
    endDate: new Date(year, month + 1, 0) // Последний день текущего месяца
  };
});
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [employees, setEmployees] = useState([]);
  const [activeView, setActiveView] = useState('overview');
  const [departments, setDepartments] = useState([]);
  const [performanceSort, setPerformanceSort] = useState('desc');

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (employees.length > 0) {
      fetchDashboardData();
    }
  }, [dateRange, selectedEmployee, employees]);

 const fetchEmployees = async () => {
  try {
    // Используем новый метод для получения только активных пользователей
    const response = await usersAPI.getActive(); // Изменено с getAll()
    
    if (response && response.users) {
      setEmployees(response.users);
      
      // Получаем отделы только из активных пользователей
      const uniqueDepartments = [...new Set(
        response.users
          .map(user => user.department)
          .filter(Boolean)
      )];
      
      setDepartments(uniqueDepartments);
    } else {
      setEmployees([]);
      setDepartments([]);
    }
  } catch (error) {
    console.error('Error fetching active employees:', error);
    setEmployees([]);
    setDepartments([]);
  }
};
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Для shift-handover всегда используем текущий день
      const today = new Date().toISOString().split('T')[0];
      
      // Основной запрос для overview с выбранным периодом
      const dashboardResponse = await statsAPI.getDashboardStats(
        dateRange.startDate.toISOString().split('T')[0],
        dateRange.endDate.toISOString().split('T')[0],
        selectedEmployee
      );

      // Отдельный запрос для shift-handover (только текущий день)
      let shiftHandoverData = {};
      if (activeView === 'shift-transfer') {
        try {
          const shiftHandoverResponse = await statsAPI.getDashboardStats(
            today,
            today,
            'all' // Для shift-handover всегда показываем всех сотрудников
          );
          shiftHandoverData = shiftHandoverResponse.shiftHandover || {};
        } catch (error) {
          console.error('Error fetching shift handover data:', error);
        }
      }

      const processedData = processApiData(dashboardResponse, shiftHandoverData);
      setDashboardData(processedData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardData(generateEmptyData());
    } finally {
      setLoading(false);
    }
  };

  const processApiData = (data, shiftHandoverData = {}) => {
    if (!data) {
    
      return generateEmptyData();
    }

    // Используем данные из chartsData которые уже отфильтрованы на сервере
    const chartData = data.chartsData || {};
    const shiftsForCharts = chartData.shifts || [];
    const tasksForCharts = chartData.tasks || [];
    const shiftRequestsForCharts = chartData.shiftRequests || [];

   

    // Расчет данных для графиков из уже отфильтрованных данных
    const shiftsByDepartment = calculateShiftsByDepartment(shiftsForCharts, employees);
    const employeePerformance = calculateEmployeePerformance(tasksForCharts, shiftsForCharts);
    const shiftTransfers = calculateShiftTransfers(shiftRequestsForCharts);
    const departmentTasks = calculateDepartmentTasks(tasksForCharts);

    // Расчет процентов для метрик
    const completionRate = data.tasks?.total > 0 ? 
      Math.round((data.tasks.completed / data.tasks.total) * 100) : 0;
    const overdueRate = data.tasks?.total > 0 ? 
      Math.round((data.tasks.overdue / data.tasks.total) * 100) : 0;

    // Обрабатываем shiftHandover с правильным количеством отделов
    const processedShiftHandover = processShiftHandoverData(
      Object.keys(shiftHandoverData).length > 0 ? { shiftHandover: shiftHandoverData } : data, 
      departments
    );

    const processedData = {
      // Основные метрики
      shifts: data.shifts || { total: 0, today: 0, completed: 0, cancelled: 0 },
      tasks: {
        ...(data.tasks || {}),
        completionRate: completionRate,
        overdueRate: overdueRate
      },
      shiftTransfers: shiftTransfers,
      
      // Данные для графиков (уже отфильтрованы на сервере)
      shiftsByDepartment,
      employeePerformance,
      departmentTasks,
      
      // Данные для передачи смен
      shiftHandover: processedShiftHandover
    };

  
    return processedData;
  };

  // Обработка данных для shift-handover с правильным количеством отделов
  const processShiftHandoverData = (data, departments) => {
    const originalShiftHandover = data.shiftHandover || {
      departmentStats: [],
      overallStatus: {
        isSystemReady: true,
        currentShift: 'day',
        currentTime: new Date().toLocaleTimeString('ru-RU'),
        checkTime: new Date().toISOString()
      }
    };

    // Используем реальные отделы из departments
    const departmentStats = departments.map(dept => {
      // Ищем статистику для этого отдела
      const existingDept = originalShiftHandover.departmentStats?.find(d => d.department === dept);
      
      if (existingDept) {
        return existingDept;
      }
      
      // Если нет данных для отдела, создаем пустые
      return {
        department: dept,
        dayShift: { 
          completed: 0, 
          total: 0, 
          successRate: 100, 
          isSuccessful: true,
          isShiftEnded: false
        },
        nightShift: { 
          completed: 0, 
          total: 0, 
          successRate: 100, 
          isSuccessful: true,
          isShiftEnded: false
        }
      };
    });

    // Рассчитываем общий статус системы на основе реальных отделов
    const readyDepartmentsDay = departmentStats.filter(dept => dept.dayShift.isSuccessful).length;
    const readyDepartmentsNight = departmentStats.filter(dept => dept.nightShift.isSuccessful).length;
    
    const isSystemReady = departmentStats.every(dept => 
      dept.dayShift.isSuccessful && dept.nightShift.isSuccessful
    );

    const currentHour = new Date().getHours();
    const currentShift = currentHour >= 8 && currentHour < 20 ? 'day' : 'night';

    return {
      departmentStats: departmentStats,
      historicalTasks: originalShiftHandover.historicalTasks || [],
      overallStatus: {
        isSystemReady: isSystemReady,
        dayShift: { 
          isSuccessful: readyDepartmentsDay === departments.length,
          totalDepartments: departments.length,
          readyDepartments: readyDepartmentsDay
        },
        nightShift: { 
          isSuccessful: readyDepartmentsNight === departments.length,
          totalDepartments: departments.length,
          readyDepartments: readyDepartmentsNight
        },
        currentShift: currentShift,
        currentTime: new Date().toLocaleTimeString('ru-RU'),
        checkTime: new Date().toISOString()
      }
    };
  };
const employeeOptions = [
  { value: 'all', label: 'Все сотрудники' },
  ...employees.map(emp => ({ // employees уже отфильтрованы
    value: emp.id.toString(),
    label: `${emp.username} (${emp.department || 'Без отдела'})`
  }))
];
  const calculateShiftsByDepartment = (shifts, employees) => {
    const departmentShifts = {};
    
    shifts.forEach(shift => {
      const employee = employees.find(emp => emp.id === shift.user_id);
      const department = employee?.department || 'Неизвестно';
      
      if (!departmentShifts[department]) {
        departmentShifts[department] = 0;
      }
      departmentShifts[department]++;
    });

    if (Object.keys(departmentShifts).length === 0) {
      return departments.map(dept => ({
        name: dept,
        shifts: 0
      }));
    }

    return Object.keys(departmentShifts).map(dept => ({
      name: dept,
      shifts: departmentShifts[dept]
    }));
  };

const calculateEmployeePerformance = (tasks, shifts) => {
  if (employees.length === 0) return [];

  const performanceMap = {};
  
  // Инициализация данных для всех сотрудников
  employees.forEach(emp => {
    performanceMap[emp.id] = {
      id: emp.id,
      username: emp.username,
      department: emp.department,
      completedTasks: 0,
      totalTasks: 0,
      shiftCount: 0,
      completionRate: 0
    };
  });

  // Подсчет задач по отделам - ВСЕ задачи отдела учитываются для ВСЕХ сотрудников отдела
  tasks.forEach(task => {
    const taskDepartment = task.department;
    
    if (!taskDepartment) return;
    
    // Находим всех сотрудников этого отдела
    const departmentEmployees = employees.filter(emp => emp.department === taskDepartment);
    
    // Учитываем задачу для каждого сотрудника отдела
    departmentEmployees.forEach(emp => {
      if (performanceMap[emp.id]) {
        performanceMap[emp.id].totalTasks++;
        if (task.status === 'completed') {
          performanceMap[emp.id].completedTasks++;
        }
      }
    });
  });

  // Подсчет смен (остается как было)
  shifts.forEach(shift => {
    if (performanceMap[shift.user_id]) {
      performanceMap[shift.user_id].shiftCount++;
    }
  });

  // Расчет процента выполнения
  Object.keys(performanceMap).forEach(empId => {
    const emp = performanceMap[empId];
    emp.completionRate = emp.totalTasks > 0 ? Math.round((emp.completedTasks / emp.totalTasks) * 100) : 0;
  });

  let result = Object.values(performanceMap);
  
  if (selectedEmployee !== 'all') {
    result = result.filter(emp => emp.id.toString() === selectedEmployee);
  }

 
  return result;
};

  const calculateShiftTransfers = (shiftRequests) => {
    const transfers = {
      total: shiftRequests.length,
      approved: shiftRequests.filter(req => req.status === 'approved').length,
      pending: shiftRequests.filter(req => req.status === 'pending').length,
      rejected: shiftRequests.filter(req => req.status === 'rejected').length,
      dailyStats: []
    };

    return transfers;
  };

const calculateDepartmentTasks = (tasks) => {

  
  const departmentTasks = {};
  
  tasks.forEach(task => {
    let department = task.department;
    
    if (!department || department.trim() === '') {
      const employee = employees.find(emp => emp.id === task.assignee_id);
      department = employee?.department || 'Неизвестно';
    }
    
    if (!department || department.trim() === '') {
      department = 'Неизвестно';
    }
    
    if (!departmentTasks[department]) {
      departmentTasks[department] = {
        name: department,
        taskCount: 0,
        completed: 0,
        inProgress: 0,
        pending: 0,
        overdue: 0
      };
    }
    
    departmentTasks[department].taskCount++;
    
    // ОТЛАДКА для каждой задачи
    const isOverdue = task.due_date && new Date(task.due_date) < new Date();

    
    // СЕРВЕРНАЯ ЛОГИКА: если задача просрочена и не завершена - ТОЛЬКО в просроченные
    if (isOverdue && task.status !== 'completed') {
      departmentTasks[department].overdue++;

    } else {
      // Только НЕпросроченные задачи идут в обычные статусы
      switch (task.status) {
        case 'completed':
          departmentTasks[department].completed++;
          break;
        case 'in_progress':
          departmentTasks[department].inProgress++;
          break;
        case 'pending':
          departmentTasks[department].pending++;
          break;
        // Другие статусы не учитываются
      }
    }
  });

 

  // Создаем результат с ВСЕМИ отделами, даже если в них нет задач
  const allDepartmentsWithTasks = [];
  
  Object.values(departmentTasks).forEach(dept => {
    allDepartmentsWithTasks.push(dept);
  });
  
  departments.forEach(deptName => {
    const hasTasks = allDepartmentsWithTasks.some(dept => dept.name === deptName);
    if (!hasTasks) {
      allDepartmentsWithTasks.push({
        name: deptName,
        taskCount: 0,
        completed: 0,
        inProgress: 0,
        pending: 0,
        overdue: 0
      });
    }
  });

  return allDepartmentsWithTasks;
};

  const generateEmptyData = () => {
    return {
      shifts: { total: 0, today: 0, completed: 0, cancelled: 0 },
      tasks: { total: 0, completed: 0, inProgress: 0, pending: 0, overdue: 0, completionRate: 0, overdueRate: 0 },
      shiftTransfers: { total: 0, approved: 0, pending: 0, rejected: 0, dailyStats: [] },
      shiftsByDepartment: [],
      employeePerformance: [],
      departmentTasks: [],
      shiftHandover: {
        departmentStats: [],
        overallStatus: {
          isSystemReady: true,
          dayShift: { 
            isSuccessful: true, 
            totalDepartments: departments.length, 
            readyDepartments: departments.length 
          },
          nightShift: { 
            isSuccessful: true, 
            totalDepartments: departments.length, 
            readyDepartments: departments.length 
          },
          currentShift: 'day',
          currentTime: new Date().toLocaleTimeString('ru-RU'),
          checkTime: new Date().toISOString()
        }
      }
    };
  };

  const handleDateRangeChange = (start, end) => {
    setDateRange({ startDate: start, endDate: end });
  };

  const handleViewChange = (view) => {
    setActiveView(view);
    // При переключении на shift-transfer обновляем данные
    if (view === 'shift-transfer') {
      setTimeout(() => {
        fetchDashboardData();
      }, 100);
    }
  };

  const hasData = dashboardData && (
    dashboardData.shifts.total > 0 ||
    dashboardData.tasks.total > 0 ||
    dashboardData.shiftTransfers.total > 0 ||
    (dashboardData.departmentTasks && dashboardData.departmentTasks.some(dept => dept.taskCount > 0)) ||
    (dashboardData.shiftsByDepartment && dashboardData.shiftsByDepartment.some(dept => dept.shifts > 0)) ||
    (dashboardData.employeePerformance && dashboardData.employeePerformance.length > 0) ||
    (activeView === 'shift-transfer' && dashboardData.shiftHandover?.departmentStats?.length > 0)
  );

  const renderMetricsOverview = () => {
    if (!dashboardData) return null;

    const { shifts, tasks, shiftTransfers } = dashboardData;

    const completionRate = tasks.completionRate || (tasks.total > 0 ? Math.round((tasks.completed / tasks.total) * 100) : 0);
    const overdueRate = tasks.overdueRate || (tasks.total > 0 ? Math.round((tasks.overdue / tasks.total) * 100) : 0);
    const approvalRate = shiftTransfers.total > 0 ? Math.round((shiftTransfers.approved / shiftTransfers.total) * 100) : 0;

    return (
      <div className="metrics-overview">
        <MetricCard
          title="Всего смен"
          value={shifts.total || 0}
          trend={shifts.today ? `+${shifts.today} сегодня` : 'Нет данных'}
          trendType={shifts.today > 0 ? "positive" : ""}
          icon="calendar-day"
          color="primary"
        />
        
        <MetricCard
          title="Всего задач"
          value={tasks.total || 0}
          trend={`${completionRate}% выполнено`}
          icon="tasks"
          color="info"
        />
        
        <MetricCard
          title="Выполнено задач"
          value={tasks.completed || 0}
          trend={`${completionRate}%`}
          icon="check-circle"
          color="success"
        />
     
        <MetricCard
          title="Передач смен"
          value={shiftTransfers.total || 0}
          trend={`${approvalRate}% подтверждено`}
          icon="exchange-alt"
          color="secondary"
        />

        <MetricCard
          title="Просроченных задач"
          value={tasks.overdue || 0}
          trend={`${overdueRate}%`}
          trendType={tasks.overdue > 0 ? "negative" : ""}
          icon="exclamation-triangle"
          color="warning"
        />
      </div>
    );
  };

  const renderShiftStatsByDepartment = () => {
    const departmentData = dashboardData?.shiftsByDepartment || [];

    if (departmentData.length === 0 || departmentData.every(dept => dept.shifts === 0)) {
      return (
        <div className="chart-container full-width">
          <div className="chart-header">
            <h3><i className="fas fa-calendar-week"></i>Статистика смен по отделам</h3>
            <div className="chart-period">
              {dateRange.startDate.toLocaleDateString()} - {dateRange.endDate.toLocaleDateString()}
              {selectedEmployee !== 'all' && <span className="employee-filter"> • Фильтр по сотруднику</span>}
            </div>
          </div>
          <div className="no-chart-data">
            <i className="fas fa-calendar-week"></i>
            <p>Нет данных о сменах по отделам за выбранный период</p>
            {selectedEmployee !== 'all' && (
              <p className="filter-notice">Попробуйте сбросить фильтр сотрудника</p>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="chart-container full-width">
        <div className="chart-header">
          <h3><i className="fas fa-calendar-week"></i>Статистика смен по отделам</h3>
          <div className="chart-period">
            {dateRange.startDate.toLocaleDateString()} - {dateRange.endDate.toLocaleDateString()}
            {selectedEmployee !== 'all' && <span className="employee-filter"> • Фильтр по сотруднику</span>}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={departmentData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="shifts" fill="#3b82f6" name="Количество смен" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

    const renderEmployeePerformance = () => {
      const performanceData = dashboardData?.employeePerformance || [];
      const sortedPerformanceData = [...performanceData].sort((a, b) => {
        const totalA = (a.shiftCount || 0) + (a.completedTasks || 0);
        const totalB = (b.shiftCount || 0) + (b.completedTasks || 0);
        return performanceSort === 'asc' ? totalA - totalB : totalB - totalA;
      });

  

    if (performanceData.length === 0) {
      return (
        <div className="chart-container">
          <div className="chart-header">
            <h3>
              <i className="fas fa-users"></i>
              Производительность сотрудников
              {selectedEmployee !== 'all' && <span className="filter-badge">фильтр</span>}
            </h3>
          </div>
          <div className="no-chart-data">
            <i className="fas fa-users"></i>
            <p>Нет данных о производительности за выбранный период</p>
            {selectedEmployee !== 'all' && (
              <p className="filter-notice">Попробуйте сбросить фильтр сотрудника</p>
            )}
          </div>
        </div>
      );
    }

 return (
    <div className="chart-container">
        <div className="chart-header">
          <h3>
            <i className="fas fa-users"></i>
            Производительность сотрудников
            {selectedEmployee !== 'all' && <span className="filter-badge">фильтр</span>}
          </h3>
          <button
            className="sort-toggle"
            onClick={() => setPerformanceSort(prev => (prev === 'desc' ? 'asc' : 'desc'))}
            title={performanceSort === 'desc' ? 'Сортировка по убыванию' : 'Сортировка по возрастанию'}
          >
            <i className={`fas ${performanceSort === 'desc' ? 'fa-sort-amount-down-alt' : 'fa-sort-amount-up-alt'}`}></i>
          </button>
        </div>
        <div className="performance-scroll">
          <ResponsiveContainer width="100%" height={Math.max(300, sortedPerformanceData.length * 44)}>
            <BarChart 
              data={sortedPerformanceData} 
              layout="vertical" 
              margin={{ left: 100, right: 20, bottom: 24 }}
              barCategoryGap={24}
              barGap={10}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis 
                type="category" 
                dataKey="username" 
                width={80} 
                tick={{ fontSize: 12 }} 
              />
              <Tooltip 
                formatter={(value, name) => {
          
                  if (name === 'completionRate') return [`${value}%`, 'Процент выполнения'];
                  return [value, name];
                }} 
              />
              <Legend />
              <Bar 
                dataKey="completedTasks" 
                fill="#10b981" 
                name="Выполнено задач" 
                radius={[0, 4, 4, 0]} 
              />
              <Bar 
                dataKey="shiftCount" 
                fill="#8b5cf6" 
                name="Количество смен" 
                radius={[0, 4, 4, 0]} 
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderShiftTransfers = () => {
    const transferData = [
      { name: 'Подтверждено', value: dashboardData?.shiftTransfers?.approved || 0, color: '#10b981' },
      { name: 'Ожидает', value: dashboardData?.shiftTransfers?.pending || 0, color: '#f59e0b' },
      { name: 'Отклонено', value: dashboardData?.shiftTransfers?.rejected || 0, color: '#ef4444' }
    ];

    const totalTransfers = transferData.reduce((sum, item) => sum + item.value, 0);

    if (totalTransfers === 0) {
      return (
        <div className="chart-container">
          <div className="chart-header">
            <h3><i className="fas fa-exchange-alt"></i>Передачи смен</h3>
          </div>
          <div className="no-chart-data">
            <i className="fas fa-exchange-alt"></i>
            <p>Нет данных о передачах смен за выбранный период</p>
          </div>
        </div>
      );
    }

    return (
      <div className="chart-container">
        <div className="chart-header">
          <h3><i className="fas fa-exchange-alt"></i>Передачи смен</h3>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie 
              data={transferData} 
              cx="50%" 
              cy="50%" 
              innerRadius={60} 
              outerRadius={100} 
              paddingAngle={5} 
              dataKey="value"
              label={({ name, value }) => `${name}: ${value}`}
            >
              {transferData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderDepartmentTasks = () => {
    const departmentData = dashboardData?.departmentTasks || [];

    if (departmentData.length === 0 || departmentData.every(dept => dept.taskCount === 0)) {
      return (
        <div className="chart-container full-width">
          <div className="chart-header">
            <h3><i className="fas fa-building"></i>Задачи по отделам</h3>
          </div>
          <div className="no-chart-data">
            <i className="fas fa-building"></i>
            <p>Нет данных о задачах по отделам за выбранный период</p>
          </div>
        </div>
      );
    }

    return (
      <div className="chart-container full-width">
        <div className="chart-header">
          <h3><i className="fas fa-building"></i>Задачи по отделам</h3>
        </div>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={departmentData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="completed" fill="#10b981" name="Выполнено" stackId="a" radius={[4, 4, 0, 0]} />
            <Bar dataKey="inProgress" fill="#3b82f6" name="В работе" stackId="a" />
            <Bar dataKey="pending" fill="#f59e0b" name="Ожидает" stackId="a" />
            <Bar dataKey="overdue" fill="#ef4444" name="Просрочено" stackId="a" radius={[0, 0, 4, 4]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderShiftHandoverDashboard = () => {

    
    if (!dashboardData?.shiftHandover?.overallStatus) {
      return (
        <div className="shift-handover-dashboard">
          <div className="no-data-message">
            <i className="fas fa-exchange-alt"></i>
            <div>
              <strong>Данные о передаче смен отсутствуют</strong>
              <p>Информация о готовности смен к передаче временно недоступна</p>
            </div>
          </div>
        </div>
      );
    }

    const { overallStatus, departmentStats } = dashboardData.shiftHandover;
    const currentShiftName = overallStatus.currentShift === 'day' ? 'Дневная смена (8:00-20:00)' : 'Ночная смена (20:00-8:00)';
    const today = new Date().toLocaleDateString('ru-RU');

    return (
      <div className="shift-handover-dashboard">
        <div className="content-page-header">
          <h2><i className="fas fa-exchange-alt"></i>Готовность к передаче смены</h2>
          <div className="current-date">
            {today} | {currentShiftName}
          </div>
          <div className="filter-info shift-handover-info">
            ⓘ Данные отображаются только за текущий день
          </div>
        </div>

        <div className={`system-status ${overallStatus?.isSystemReady ? 'ready' : 'not-ready'}`}>
          <div className="status-header">
            <i className={`fas ${overallStatus?.isSystemReady ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
            <h3>Общий статус системы</h3>
            <span className="status-badge">
              {overallStatus?.isSystemReady ? '✅ Все смены готовы' : '❌ Требуется внимание'}
            </span>
          </div>
          <div className="status-details">
            <div className="status-item">
              <span>Дневная смена:</span>
              <span className={`status ${overallStatus?.dayShift?.isSuccessful ? 'success' : 'error'}`}>
                {overallStatus?.dayShift?.readyDepartments || 0}/{overallStatus?.dayShift?.totalDepartments || 0} отделов готово
              </span>
            </div>
            <div className="status-item">
              <span>Ночная смена:</span>
              <span className={`status ${overallStatus?.nightShift?.isSuccessful ? 'success' : 'error'}`}>
                {overallStatus?.nightShift?.readyDepartments || 0}/{overallStatus?.nightShift?.totalDepartments || 0} отделов готово
              </span>
            </div>
            <div className="status-time">
              Последняя проверка: {overallStatus?.checkTime ? new Date(overallStatus.checkTime).toLocaleTimeString('ru-RU') : 'Неизвестно'}
            </div>
          </div>
        </div>

        <div className="departments-readiness">
          <h3>Готовность отделов</h3>
          <div className="readiness-grid">
            {(departmentStats || []).map((dept) => (
              <div key={dept.department} className={`readiness-card ${
                dept?.dayShift?.isSuccessful && dept?.nightShift?.isSuccessful ? 'ready' : 'not-ready'
              }`}>
                <div className="card-header">
                  <i className="fas fa-building"></i>
                  <h4>{dept.department}</h4>
                  <span className="department-status">
                    {dept?.dayShift?.isSuccessful && dept?.nightShift?.isSuccessful ? '✅ Готов' : '❌ Не готов'}
                  </span>
                </div>

                <div className="shift-statuss">
                  <div className="shift-info">
                    <div className="shift-header">
                      <span>Дневная смена</span>
                      <span className="shift-time">8:00-20:00</span>
                    </div>
                    <div className="progress-container">
                      <div className="progress-text">
                        {dept?.dayShift?.completed || 0}/{dept?.dayShift?.total || 0} задач
                      </div>
                      <div className="progress-bar">
                        <div 
                          className={`progress-fill ${
                            dept?.dayShift?.isSuccessful ? 'success' : 'error'
                          }`}
                          style={{ width: `${Math.min(dept?.dayShift?.successRate || 0, 100)}%` }}
                        ></div>
                      </div>
                      <div className="progress-percent">{dept?.dayShift?.successRate || 0}%</div>
                    </div>
                    {!dept?.dayShift?.isSuccessful && dept?.dayShift?.isShiftEnded && (
                      <div className="warning">⚠️ Не все задачи выполнены</div>
                    )}
                  </div>

                  <div className="shift-info">
                    <div className="shift-header">
                      <span>Ночная смена</span>
                      <span className="shift-time">20:00-8:00</span>
                    </div>
                    <div className="progress-container">
                      <div className="progress-text">
                        {dept?.nightShift?.completed || 0}/{dept?.nightShift?.total || 0} задач
                      </div>
                      <div className="progress-bar">
                        <div 
                          className={`progress-fill ${
                            dept?.nightShift?.isSuccessful ? 'success' : 'error'
                          }`}
                          style={{ width: `${Math.min(dept?.nightShift?.successRate || 0, 100)}%` }}
                        ></div>
                      </div>
                      <div className="progress-percent">{dept?.nightShift?.successRate || 0}%</div>
                    </div>
                    {!dept?.nightShift?.isSuccessful && dept?.nightShift?.isShiftEnded && (
                      <div className="warning">⚠️ Не все задачи выполнены</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="loading">
          <i className="fas fa-spinner fa-spin"></i>
          Загрузка аналитики...
        </div>
      </div>
    );
  }

 return (
    <div className="dashboard-page">
      <div className="content-page-header">
        <div className="filter-group">
          <label>Период:</label>
          <DateRangePicker 
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={handleDateRangeChange} // ← Используется импортированный компонент
          />
        </div>
        <div className="view-toggle">
          <button 
            className={`toggle-btn ${activeView === 'overview' ? 'active' : ''}`}
            onClick={() => handleViewChange('overview')}
          >
            <i className="fas fa-chart-bar"></i>
            Обзор
          </button>
          <button 
            className={`toggle-btn ${activeView === 'shift-transfer' ? 'active' : ''}`}
            onClick={() => handleViewChange('shift-transfer')}
          >
            <i className="fas fa-exchange-alt"></i>
            Передача смены
          </button>
        </div>
        <div className="filter-group">
          <div className="filter-emloy">
            <label>Сотрудник:</label>
            <CustomDropdown
              options={employeeOptions}
              value={selectedEmployee}
              onChange={setSelectedEmployee}
              placeholder="Выберите сотрудника..."
            />
          </div>
        </div>
      </div>

      {!hasData && (
        <div className="no-data-message">
          <i className="fas fa-database"></i>
          <div>
            <strong>Данные отсутствуют</strong>
            <p>В системе пока нет данных для отображения за выбранный период. Начните добавлять смены и задачи.</p>
            {selectedEmployee !== 'all' && activeView === 'overview' && (
              <p className="filter-notice">Или попробуйте сбросить фильтр сотрудника</p>
            )}
          </div>
        </div>
      )}

      {activeView === 'overview' && hasData && (
        <>
          {renderMetricsOverview()}
          <div className="charts-grid">
            {renderShiftStatsByDepartment()}
            {renderDepartmentTasks()}
            {renderEmployeePerformance()}
            {renderShiftTransfers()}
          </div>
        </>
      )}

      {activeView === 'shift-transfer' && renderShiftHandoverDashboard()}
    </div>
  );
};

const MetricCard = ({ title, value, trend, trendType, icon, color }) => {
  const displayValue = value || 0;
  const displayTrend = typeof trend === 'number' ? `${trend}%` : trend;

  return (
    <div className={`metric-card ${color}`}>
      <div className="metric-icon">
        <i className={`fas fa-${icon}`}></i>
      </div>
      <div className="metric-content">
        <div className="metric-value">{displayValue}</div>
        <div className="metric-title">{title}</div>
        {trend && <span className={`metric-trend ${trendType || ''}`}>{displayTrend}</span>}
      </div>
    </div>
  );
};


export default Dashboard;
